<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');
const uiFlags = useMapGetter('contacts/getUIFlags');
const UNASSIGNED = 'unassigned';
const selectedTeamId = ref(props.contact?.team?.id || UNASSIGNED);
const pickerVersion = ref(0);

const isAssigning = computed(() => uiFlags.value.isAssigningTeam);
const teamOptions = computed(() => [
  {
    value: UNASSIGNED,
    label: t('CONTACTS_LAYOUT.DETAILS.TEAM_ASSIGNMENT.UNASSIGNED'),
  },
  ...teams.value.map(team => ({ value: team.id, label: team.name })),
]);

watch(
  () => props.contact?.team?.id,
  teamId => {
    selectedTeamId.value = teamId || UNASSIGNED;
  }
);

onMounted(async () => {
  if (teams.value.length) return;

  try {
    await store.dispatch('teams/get');
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.TEAM_ASSIGNMENT.LOAD_ERROR'));
  }
});

const updateTeamAssignment = async teamId => {
  if (!teamId || teamId === selectedTeamId.value) {
    pickerVersion.value += 1;
    return;
  }
  const previousTeamId = selectedTeamId.value;
  selectedTeamId.value = teamId;

  try {
    await store.dispatch('contacts/assignTeam', {
      id: props.contact.id,
      teamId: teamId === UNASSIGNED ? null : teamId,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.TEAM_ASSIGNMENT.SUCCESS'));
  } catch {
    selectedTeamId.value = previousTeamId;
    useAlert(t('CONTACTS_LAYOUT.DETAILS.TEAM_ASSIGNMENT.ERROR'));
    pickerVersion.value += 1;
  }
};
</script>

<template>
  <div class="flex w-full flex-col gap-2">
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.TEAM_ASSIGNMENT.LABEL') }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.DETAILS.TEAM_ASSIGNMENT.DESCRIPTION') }}
      </span>
    </div>
    <ComboBox
      :key="pickerVersion"
      :model-value="selectedTeamId"
      :options="teamOptions"
      :disabled="isAssigning"
      class="w-full [&>div>button]:!h-10 [&>div>button]:!rounded-lg [&>div>button]:!bg-white [&>div>button]:!text-sm"
      @update:model-value="updateTeamAssignment"
    />
  </div>
</template>
